import React, { useState, useContext, useEffect, useRef } from "react";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material/styles";
import { logoForBg } from "../helpers/logoSrc";
import {
  Drawer, AppBar, Toolbar, List, Typography,
  MenuItem, IconButton, Menu, Avatar, Tooltip,
  Dialog, DialogTitle, DialogContent, Table, TableBody, TableRow, TableCell,
} from "@mui/material";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import useKeyboardShortcuts from "../hooks/useKeyboardShortcuts";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PaletteIcon from "@mui/icons-material/Palette";
import useSound from "use-sound";
import alertSound from "../assets/sound.mp3";

import api from "../services/api";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import DriveFolderPrompt from "../components/DriveFolderPrompt";
import UserModal from "../components/UserModal";
import GlobalSearchDialog from "../components/GlobalSearchDialog";
import ThemePickerDialog from "../components/ThemePickerDialog";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import ThemeDecor from "../themes/decor/ThemeDecor";
import useInactivityTimer from "../hooks/useInactivityTimer";
import openSocket from "../services/socket-io";
import { toast } from "react-toastify";
import { useVersionCheck } from "../hooks/useVersionCheck";

const soundEnabled = () => localStorage.getItem("whaticket_sound_enabled") !== "false";
const pushEnabled = () => localStorage.getItem("whaticket_push_enabled") !== "false";

const DRAWER_WIDTH = 220;
const DRAWER_COLLAPSED = 56;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    position: "relative",
    height: "100vh",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.mode === "dark" ? "#0D1117" : "#0F172A",
    borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.08)"}`,
    boxShadow: "none",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  toolbar: {
    paddingRight: 12,
    paddingLeft: 12,
    minHeight: "48px !important",
  },

  menuButton: {
    color: "rgba(255,255,255,0.7)",
    marginRight: 8,
    "&:hover": { color: "#FFFFFF", backgroundColor: "rgba(255,255,255,0.08)" },
  },
  menuButtonHidden: { display: "none" },

  appBarTitle: {
    flexGrow: 1,
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "0.875rem",
    letterSpacing: "0.01em",
    opacity: 0.9,
  },

  appBarIcon: {
    color: "rgba(255,255,255,0.7)",
    "&:hover": { color: "#FFFFFF", backgroundColor: "rgba(255,255,255,0.08)" },
  },

  drawerPaper: {
    position: "relative",   // mantém o drawer no fluxo flex (MUI v6 default é fixed)
    width: DRAWER_WIDTH,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    position: "relative",
    width: DRAWER_COLLAPSED,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  drawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px 0 14px",
    minHeight: "48px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  drawerLogo: {
    height: "28px",
    objectFit: "contain",
    opacity: 0.9,
  },
  drawerCollapseBtn: {
    color: theme.palette.text.secondary,
    padding: 6,
    borderRadius: 6,
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
  },
  drawerFooter: {
    marginTop: "auto",
    padding: "10px 14px",
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: "10px",
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
    overflow: "hidden",
    opacity: 0.5,
  },

  appBarSpacer: { minHeight: "48px" },
  content: {
    flex: 1,
    height: "100%",
    minHeight: 0,
    overflow: "auto",
    // Acima do ThemeDecor (fixed, zIndex 0): conteúdo sempre por cima da
    // decoração. Com decor ativo o fundo fica transparente — o body já pinta
    // background.default (CssBaseline), então o padrão (patinhas/estrelas)
    // aparece nas áreas de respiro sem mudar nada nos temas sem decor.
    position: "relative",
    zIndex: 1,
    backgroundColor: theme.custom?.decorId ? "transparent" : theme.palette.background.default,
    ...theme.scrollbarStyles,
  },

  versionBanner: {
    position: "fixed",
    top: 0, left: 0, right: 0,
    zIndex: 2000,
    background: "#F37021",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: "7px 16px",
    fontSize: "0.8125rem",
    fontWeight: 500,
  },
  versionBannerBtn: {
    background: "#fff",
    color: "#F37021",
    border: "none",
    borderRadius: 5,
    padding: "3px 12px",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.75rem",
    fontFamily: "inherit",
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);

  useKeyboardShortcuts([
    { key: "?", shift: true, action: () => setShortcutsOpen(true) },
    { key: "k", ctrl: true, action: () => setGlobalSearchOpen(true) },
  ]);
  useInactivityTimer(handleLogout);
  const { updateAvailable } = useVersionCheck();
  const [playAlert] = useSound(alertSound);
  const playAlertRef = useRef();
  useEffect(() => { playAlertRef.current = playAlert; }, [playAlert]);
  const disconnectedSessions = useRef(new Set());
  const alertIntervalRef = useRef(null);
  const staleIntervalRef = useRef(null);
  const lastStaleAlertRef = useRef(0);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    api.get("/settings").then(({ data }) => {
      const get = (key) => data.find(s => s.key === key)?.value;
      if (get("slaWarningMinutes")) localStorage.setItem("sla_warning_minutes", get("slaWarningMinutes"));
      if (get("slaCriticalMinutes")) localStorage.setItem("sla_critical_minutes", get("slaCriticalMinutes"));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const socket = openSocket();
    socket.on("appMessage", (data) => {
      if (
        data.action === "create" &&
        data.message &&
        !data.message.fromMe &&
        !document.hasFocus() &&
        // iOS Safari não tem a API Notification — sem esta guarda, o handler
        // estoura ReferenceError a cada mensagem recebida com a aba desfocada.
        "Notification" in window &&
        Notification.permission === "granted" &&
        pushEnabled()
      ) {
        const contact = data.ticket?.contact;
        // LGPD/SEC-004: não expor o conteúdo da mensagem (pode conter CPF, dados
        // bancários etc.) no centro de notificações do SO. O operador clica para
        // ver o conteúdo dentro da aplicação autenticada.
        const n = new Notification(contact?.name || "Nova mensagem", {
          body: "Nova mensagem recebida — clique para abrir",
          icon: contact?.profilePicUrl || "/favicon.ico",
          tag: `ticket-${data.ticket?.id}`,
        });
        n.onclick = () => { window.focus(); n.close(); };
      }
    });
    socket.on("whatsappSession", (data) => {
      if (data.action === "update" && data.session) {
        const { id, status } = data.session;
        if (status === "DISCONNECTED" || status === "qrcode") {
          disconnectedSessions.current.add(id);
        } else {
          disconnectedSessions.current.delete(id);
        }
      }
    });
    socket.on("incomingCall", (data) => {
      toast.info(
        `📞 Ligação recebida de ${data.peerJid?.replace(/@.*/, "") || "contato desconhecido"} — chamada rejeitada automaticamente`,
        { autoClose: 8000 }
      );
      if (playAlertRef.current && soundEnabled()) playAlertRef.current();
    });
    alertIntervalRef.current = setInterval(() => {
      if (disconnectedSessions.current.size > 0) {
        const key = "wa_disconnect_alert_ts";
        const last = Number(localStorage.getItem(key) || 0);
        if (Date.now() - last > 15 * 60 * 1000) {
          localStorage.setItem(key, String(Date.now()));
          toast.warn("WhatsApp desconectado! Acesse Conexões para reconectar.", { autoClose: 10000 });
        }
      }
    }, 10 * 60 * 1000);
    return () => { socket.disconnect(); clearInterval(alertIntervalRef.current); };
  }, []);

  useEffect(() => {
    const checkStale = async () => {
      try {
        const { data } = await api.get("/tickets/stale");
        if (data.count > 0 && Date.now() - lastStaleAlertRef.current > 10 * 60 * 1000) {
          lastStaleAlertRef.current = Date.now();
          toast.warn(`${data.count} cliente(s) aguardando há mais de 1 hora na Fila Geral`, { autoClose: 12000 });
          if (playAlertRef.current && soundEnabled()) playAlertRef.current();
        }
      } catch (_) {}
    };
    staleIntervalRef.current = setInterval(checkStale, 2 * 60 * 1000);
    checkStale();
    return () => clearInterval(staleIntervalRef.current);
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth > 600) setDrawerOpen(true);
  }, []);

  useEffect(() => {
    setDrawerVariant(document.body.offsetWidth < 600 ? "temporary" : "permanent");
  }, [drawerOpen]);

  if (loading) return <BackdropLoading />;

  return (
    <div className={classes.root}>
      <ThemeDecor variant="app" />
      {updateAvailable && (
        <div className={classes.versionBanner}>
          <span>⚡ Nova versão disponível</span>
          <button className={classes.versionBannerBtn} onClick={() => window.location.reload()}>
            Atualizar agora
          </button>
        </div>
      )}

      <Drawer
        variant={drawerVariant}
        open={drawerOpen}
        classes={{
          paper: clsx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClose),
        }}
        PaperProps={{
          style: { position: "relative", top: "auto", height: "100%" },
        }}
      >
        <div className={classes.drawerHeader}>
          {drawerOpen && (
            <img src={logoForBg(theme, theme.palette.background.paper)} alt="KR Gráfica, Carimbos e Brindes" className={classes.drawerLogo} />
          )}
          <IconButton
            size="small"
            className={classes.drawerCollapseBtn}
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            <ChevronLeftIcon
              fontSize="small"
              style={{ transform: drawerOpen ? "none" : "rotate(180deg)", transition: "transform 0.2s" }}
            />
          </IconButton>
        </div>

        <List disablePadding sx={{ pt: 0.5, flex: 1, overflow: "hidden auto", ...{} }}>
          <MainListItems
            drawerClose={() => { if (document.body.offsetWidth < 600) setDrawerOpen(false); }}
            drawerOpen={drawerOpen}
          />
        </List>

        <div className={classes.drawerFooter}>
          {drawerOpen ? `v${__APP_VERSION__} · ChatKR` : `v${__APP_VERSION__}`}
        </div>
      </Drawer>

      <UserModal open={userModalOpen} onClose={() => setUserModalOpen(false)} userId={user?.id} />
      <GlobalSearchDialog open={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} />

      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        elevation={0}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            size="small"
            aria-label="abrir menu"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(classes.menuButton, drawerOpen && classes.menuButtonHidden)}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          <Typography className={classes.appBarTitle} noWrap>
            ChatKR
          </Typography>

          <Tooltip title="Selecionar tema">
            <IconButton size="small" className={classes.appBarIcon} onClick={() => setThemePickerOpen(true)}>
              <PaletteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <ThemePickerDialog open={themePickerOpen} onClose={() => setThemePickerOpen(false)} />

          {user.id && <NotificationsPopOver className={classes.appBarIcon} />}

          <Tooltip title={user?.name || "Perfil"}>
            <IconButton
              size="small"
              onClick={(e) => { setAnchorEl(e.currentTarget); setMenuOpen(true); }}
              sx={{ ml: 0.5 }}
            >
              <Avatar
                src={user.profileImage || undefined}
                sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "#F37021" }}
              >
                {!user.profileImage && user.name ? user.name[0].toUpperCase() : null}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => { setAnchorEl(null); setMenuOpen(false); }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { minWidth: 160, mt: 0.5 } }}
          >
            <MenuItem onClick={() => { setUserModalOpen(true); setAnchorEl(null); setMenuOpen(false); }}>
              {i18n.t("mainDrawer.appBar.user.profile")}
            </MenuItem>
            <MenuItem onClick={() => { setShortcutsOpen(true); setAnchorEl(null); setMenuOpen(false); }}>
              <KeyboardIcon fontSize="small" style={{ marginRight: 8 }} />
              Atalhos de teclado
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); setMenuOpen(false); handleLogout(); }}>
              {i18n.t("mainDrawer.appBar.user.logout")}
            </MenuItem>
          </Menu>

          <Dialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Atalhos de teclado</DialogTitle>
            <DialogContent sx={{ pb: 2 }}>
              <Table size="small">
                <TableBody>
                  {[
                    ["Enter", "Enviar mensagem"],
                    ["Shift+Enter", "Nova linha na mensagem"],
                    ["Shift+?", "Abrir este painel"],
                    ["Ctrl+K", "Busca global (contatos e tickets)"],
                    ["Esc", "Fechar modal/dialog aberto"],
                    ["Ctrl+Z", "Desfazer texto digitado"],
                    ["/", "Buscar resposta rápida (no campo de mensagem)"],
                  ].map(([key, desc]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap", width: 1 }}>
                        <kbd style={{ background: "rgba(0,0,0,0.08)", borderRadius: 4, padding: "2px 6px", fontSize: "0.8em" }}>{key}</kbd>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>{desc}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
          </Dialog>
        </Toolbar>
      </AppBar>

      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ?? null}
      </main>
      <DriveFolderPrompt />
    </div>
  );
};

export default LoggedInLayout;
